import React, { useState, useEffect, useRef } from 'react';
import ensureMapsLoaded from './loadMaps';
import { useAuth } from './AuthContext';
import { flatApi } from './api';

// ─── SVG Icons ───
const IconArrowLeft = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>);
const IconCheck = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const IconMapPin = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>);

// ─── Constants ───
const POST_TYPES = ['FLAT', 'ROOM', 'PG', 'ANY'];
const GENDERS = ['MALE', 'FEMALE', 'ANY'];
const FURNISHING = ['FURNISHED', 'SEMI', 'UNFURNISHED'];
const OCCUPANCY = ['SINGLE', 'SHARED', 'ANY'];
const AMENITIES = ['TV', 'FRIDEG', 'KITCHEN', 'WIFI', 'MACHINE', 'AC', 'POWER_BACKUP', 'COOK', 'PARKING'];

const LABEL_MAP = {
    FLAT: 'Flat', ROOM: 'Room', PG: 'PG', ANY: 'Any',
    MALE: 'Male', FEMALE: 'Female',
    FURNISHED: 'Furnished', SEMI: 'Semi-Furnished', UNFURNISHED: 'Unfurnished',
    SINGLE: 'Single', SHARED: 'Shared',
    TV: '📺 TV', FRIDEG: '🧊 Fridge', KITCHEN: '🍳 Kitchen', WIFI: '📶 Wi-Fi',
    MACHINE: '🧺 Washing Machine', AC: '❄️ AC', POWER_BACKUP: '🔋 Power Backup',
    COOK: '👨‍🍳 Cook', PARKING: '🅿️ Parking',
};

const fieldLabel = { display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748B', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: '14px', border: '2px solid #DDE3DC', fontSize: '15px', fontWeight: '600', outline: 'none', transition: 'border-color 0.2s', background: 'white', color: '#1F2937', boxSizing: 'border-box' };

const ChipSelector = ({ label, options, selected, onSelect, multiple = false }) => (
    <div style={{ marginBottom: '24px' }}>
        <label style={fieldLabel}>{label}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {options.map(opt => {
                const isActive = multiple ? (selected || []).includes(opt) : selected === opt;
                return (
                    <div key={opt} onClick={() => {
                        if (multiple) {
                            const arr = selected || [];
                            onSelect(isActive ? arr.filter(x => x !== opt) : [...arr, opt]);
                        } else {
                            onSelect(isActive ? null : opt);
                        }
                    }}
                        style={{ padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', border: `2px solid ${isActive ? '#5B7F2C' : '#DDE3DC'}`, background: isActive ? '#F4F8D8' : 'white', color: isActive ? '#C2410C' : '#6B7280', userSelect: 'none' }}>
                        {LABEL_MAP[opt] || opt}
                    </div>
                );
            })}
        </div>
    </div>
);

const PostRequirementPage = ({ onBack, editRequirement = null }) => {
    const { tokens, refreshAccessToken } = useAuth();
    const isEditMode = !!editRequirement;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [postType, setPostType] = useState(editRequirement?.postType || null);
    const [location, setLocation] = useState(editRequirement?.location || null);
    const [occupancy, setOccupancy] = useState(editRequirement?.occupany || null);
    const [gender, setGender] = useState(editRequirement?.gender || null);
    const [furnishing, setFurnishing] = useState(editRequirement?.furnishing || null);
    const [budgetMin, setBudgetMin] = useState(editRequirement?.budgetMin?.toString() || '');
    const [budgetMax, setBudgetMax] = useState(editRequirement?.budgetMax?.toString() || '');
    const [description, setDescription] = useState(editRequirement?.description || '');
    const [hideMobile, setHideMobile] = useState(editRequirement?.isHideMobileNumber || false);
    const [amenities, setAmenities] = useState(editRequirement?.amenties || []);

    // Location search
    const [searchQuery, setSearchQuery] = useState(editRequirement?.location?.name || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const searchRef = useRef(null);
    const searchTimeout = useRef(null);

    useEffect(() => {
        ensureMapsLoaded().catch(() => {});
        const init = () => {
            if (window.google?.maps?.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                const div = document.createElement('div');
                placesService.current = new window.google.maps.places.PlacesService(div);
            }
        };
        if (window.google) init();
        else {
            const interval = setInterval(() => { if (window.google) { init(); clearInterval(interval); } }, 200);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchLocation = (value) => {
        setSearchQuery(value);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!value.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
        setShowSuggestions(true);
        searchTimeout.current = setTimeout(() => {
            if (!autocompleteService.current) return;
            autocompleteService.current.getPlacePredictions(
                { input: value, componentRestrictions: { country: 'in' } },
                (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) setSuggestions(predictions);
                    else setSuggestions([]);
                }
            );
        }, 400);
    };

    const handleSelectLocation = (suggestion) => {
        setSearchQuery(suggestion.description);
        setShowSuggestions(false);
        if (!placesService.current) return;
        placesService.current.getDetails({ placeId: suggestion.place_id, fields: ['geometry'] }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                setLocation({
                    name: suggestion.structured_formatting?.main_text || suggestion.description,
                    placeId: suggestion.place_id,
                    latitude: place.geometry.location.lat(),
                    longitude: place.geometry.location.lng(),
                });
            }
        });
    };

    const handleSubmit = async () => {
        if (!location) { setError('Please select a preferred location'); return; }

        setLoading(true);
        setError('');
        try {
            const body = {
                occupany: occupancy,
                gender,
                budgetMin: parseInt(budgetMin) || 0,
                budgetMax: parseInt(budgetMax) || 0,
                location,
                isHideMobileNumber: hideMobile,
                description,
                postType: postType || 'ANY',
                furnishing,
                amenties: amenities,
            };

            if (isEditMode) {
                await flatApi.updateRequirement(body, tokens.access);
                setSuccess('Requirement updated successfully! ✅');
            } else {
                await flatApi.postRequirement(body, tokens.access);
                await refreshAccessToken();
                setSuccess('Requirement posted successfully! 🎉');
            }
            setTimeout(() => onBack(), 1500);
        } catch (err) {
            setError(err.message || 'Failed to post requirement');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            {/* Header */}
            <header style={{ background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)', padding: '100px 20px 36px', color: 'white', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative orbs */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1 }}>
                    <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '14px', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', backdropFilter: 'blur(10px)', transition: 'background 0.2s', flexShrink: 0 }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}>
                        <IconArrowLeft />
                    </button>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '26px', fontWeight: '800', color: 'white', letterSpacing: '-0.5px' }}>{isEditMode ? 'Edit Requirement' : 'Post Requirement'}</h2>
                        <div style={{ fontSize: '14px', color: '#D9E6DC', marginTop: '4px', fontWeight: '500' }}>{isEditMode ? 'Update your preferences' : "Tell us what you're looking for"}</div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 24px 120px' }}>
                {error && <div style={{ background: '#FEF2F2', border: '2px solid #FCA5A5', color: '#B91C1C', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontWeight: '600', fontSize: '14px' }}>{error}</div>}
                {success && <div style={{ background: '#D1FAE5', border: '2px solid #6EE7B7', color: '#047857', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontWeight: '600', fontSize: '14px' }}>{success}</div>}

                <ChipSelector label="Looking For" options={POST_TYPES} selected={postType} onSelect={setPostType} />

                <div style={{ marginBottom: '24px' }} ref={searchRef}>
                    <label style={fieldLabel}>Preferred Location *</label>
                    <input type="text" value={searchQuery} onChange={e => handleSearchLocation(e.target.value)} placeholder="Search preferred area..." style={inputStyle} onFocus={() => { if (searchQuery) setShowSuggestions(true); }} />
                    {showSuggestions && suggestions.length > 0 && (
                        <div style={{ background: 'white', border: '1px solid #DDE3DC', borderRadius: '16px', marginTop: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'relative', zIndex: 50 }}>
                            {suggestions.map((s, i) => (
                                <div key={i} onClick={() => handleSelectLocation(s)} style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #E5E9E3', transition: 'background 0.15s' }} onMouseOver={e => e.currentTarget.style.background = '#F9FAFB'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                    <div style={{ color: '#5B7F2C' }}><IconMapPin /></div>
                                    <div>
                                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1F2937' }}>{s.structured_formatting?.main_text}</div>
                                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>{s.structured_formatting?.secondary_text}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', color: '#10B981', fontSize: '13px', fontWeight: '700' }}>
                            <IconCheck size={16} /> {location.name}
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <label style={fieldLabel}>Budget Range (₹)</label>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <input type="number" value={budgetMin} onChange={e => setBudgetMin(e.target.value)} placeholder="Min" style={inputStyle} />
                        <span style={{ color: '#9CA3AF', fontWeight: '700' }}>to</span>
                        <input type="number" value={budgetMax} onChange={e => setBudgetMax(e.target.value)} placeholder="Max" style={inputStyle} />
                    </div>
                </div>

                <ChipSelector label="Occupancy" options={OCCUPANCY} selected={occupancy} onSelect={setOccupancy} />
                <ChipSelector label="Gender Looking For" options={GENDERS} selected={gender} onSelect={setGender} />
                <ChipSelector label="Furnishing" options={FURNISHING} selected={furnishing} onSelect={setFurnishing} />

                <div style={{ marginBottom: '24px' }}>
                    <label style={fieldLabel}>Additional Details</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Move-in date, specific requirements, preferences..." rows={4} style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' }} />
                </div>

                <ChipSelector label="Amenities" options={AMENITIES} selected={amenities} onSelect={setAmenities} multiple />

                {/* Toggle */}
                <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                    <div>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#1F2937' }}>Hide Phone Number</div>
                        <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '2px' }}>People can only chat with you</div>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px' }}>
                        <input type="checkbox" checked={hideMobile} onChange={e => setHideMobile(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: hideMobile ? '#5B7F2C' : '#D1D5DB', borderRadius: '28px', transition: '0.3s' }} />
                        <span style={{ position: 'absolute', height: '22px', width: '22px', left: hideMobile ? '27px' : '3px', bottom: '3px', background: 'white', borderRadius: '50%', transition: '0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }} />
                    </label>
                </div>
            </div>

            {/* Bottom bar */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid #DDE3DC', padding: '16px 24px', boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 50 }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #5B7F2C, #A6C63D)', color: 'white', border: 'none', borderRadius: '16px', padding: '16px', fontWeight: '800', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: loading ? 'none' : '0 8px 24px rgba(91, 127, 44, 0.3)' }}>
                        <IconCheck size={18} /> {loading ? 'Saving...' : (isEditMode ? 'Update Requirement' : 'Post Requirement')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PostRequirementPage;
